
import Vue from 'vue';
import { formatDate, getDiffTime } from '@/helpers/date';
import { InformationModal, Loading } from '@descomplica/vue-components';
import { CourseModel } from '@descomplica/dex-models';
import { StudyPlanningModel } from '@/models/studyPlanning/StudyPlanningModel';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';
import { StudyPlanningBreakpointModel } from '@/models/studyPlanning/StudyPlanningBreakpointModel';
import { ContractExtensionEligibilityInfo } from '@/models/contractExtension/ContractExtensionEligibilityModel';
import ContractExtensionRequestService from '@/helpers/postgradContractExtension/request';
import { getChimeraToken } from '@/helpers/get-tokens';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { PostgradTrialStatus } from '@/models/postgradTrial/PostgradTrialModel';

interface Data {
  isRegisteringExtensionRequest: boolean;
}

type LabeledExtensionOption = ContractExtensionEligibilityInfo['extensionOptions'][number] & {
  label: string;
};

export default Vue.extend({
  components: {
    InformationModal,
    Loading,
  },
  data(): Data {
    return {
      isRegisteringExtensionRequest: false,
    };
  },
  computed: {
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    studyPlanning(): StudyPlanningModel {
      return this.$store.getters.studyPlanning(this.currentCourse.id);
    },
    studyPlanningExtensionBreakpoints(): StudyPlanningBreakpointModel[] {
      return this.$store.getters.studyPlanningExtensionBreakpoints;
    },
    showContractExtensionContent(): boolean {
      return !!this.extensionOptionsForStudyPlanning.length;
    },
    contractExpirationDateToConsider(): Date {
      return (
        this.currentChimeraCourse?.contractByUser?.expirationDate ??
        this.studyPlanning.contractExpirationDate
      );
    },
    contractExpirationDateFormatted(): string {
      return formatDate(this.contractExpirationDateToConsider, 'DD / MM / YYYY');
    },
    courseEligibleForExtensionInformation(): ContractExtensionEligibilityInfo | null {
      return this.$store.getters.contractExtensionEligibilityInformationByCourseId(
        this.currentCourse.id,
      );
    },
    daysToFinishContract(): number {
      const todayDate = new Date();
      return getDiffTime(this.contractExpirationDateToConsider, todayDate).days;
    },
    contractExtensionOptions(): ContractExtensionEligibilityInfo['extensionOptions'] {
      if (
        !this.courseEligibleForExtensionInformation ||
        !this.studyPlanningExtensionBreakpoints.length ||
        !this.currentChimeraCourse
      )
        return [];

      const { remainingContractPeriod } = this.currentChimeraCourse.contractByUser!;
      const minimumExtensionOptionForStudyPlanning =
        this.studyPlanningExtensionBreakpoints[0].numberOfMonths - remainingContractPeriod.months;

      return this.courseEligibleForExtensionInformation.extensionOptions.filter(
        (b) => b.period >= minimumExtensionOptionForStudyPlanning,
      );
    },
    extensionOptionsPeriods(): number[] {
      return this.contractExtensionOptions.map((o) => o.period);
    },
    extensionOptionsForStudyPlanning(): LabeledExtensionOption[] {
      const monthsLabels = this.contractExtensionOptions.map((o) => ({
        ...o,
        label: this.$tc('pluralizedElapsedTimeMonths', o.period, {
          period: o.period,
        }),
      }));

      return monthsLabels;
    },
    getTrialStatusByDescomplicaCourseId(): PostgradTrialStatus {
      return this.$store.getters.getTrialStatusByDescomplicaCourseId(
        this.currentChimeraCourse?._id,
      );
    },
  },
  created() {
    Analytics.trackEvent(EVENT_LABELS.modalViewed, {
      in_postgrad_study_planning_config_modal: true,
      with_breakpoints: false,
      extendable: this.showContractExtensionContent,
      course: this.currentCourse.name,
      category: 'pos',
      available_extension_options: this.extensionOptionsPeriods,
      available_extension_options_quantity: this.extensionOptionsPeriods.length,
      ...this.getTrialStatusByDescomplicaCourseId,
    });
  },
  methods: {
    async redirectToCheckout(extensionOption: LabeledExtensionOption): Promise<void> {
      if (
        !this.currentChimeraCourse?.contractByUser ||
        !extensionOption.url ||
        this.isRegisteringExtensionRequest
      )
        return;

      this.isRegisteringExtensionRequest = true;
      this.trackContractExtensionButtonClick(extensionOption);

      const hasRequestBeenRegistered =
        await ContractExtensionRequestService.registerContractExtensionRequest({
          course: this.currentCourse,
          chimeraContractId: +this.currentChimeraCourse.contractByUser.id,
          chimeraToken: getChimeraToken(),
          period: extensionOption.period,
        });

      if (!hasRequestBeenRegistered) {
        this.isRegisteringExtensionRequest = false;
        return;
      }

      this.trackRequestContractExtensionSuccess(extensionOption);

      window.location.replace(extensionOption.url);
    },
    trackRedirectToClassroomsPage(): void {
      Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
        in_invalid_postgrad_study_planning_modal: true,
        button_action: 'redirect_to_classrooms',
        extendable: this.showContractExtensionContent,
        course: this.currentCourse.name,
        category: 'pos',
        available_extension_options: this.extensionOptionsPeriods,
        available_extension_options_quantity: this.extensionOptionsPeriods.length,
        ...this.getTrialStatusByDescomplicaCourseId,
      });
    },
    trackRequestContractExtensionSuccess(extensionOption: LabeledExtensionOption): void {
      Analytics.trackEvent(EVENT_LABELS.postgradRequestContractExtensionSuccessful, {
        in_invalid_postgrad_study_planning_modal: true,
        days_to_finish_contract: this.daysToFinishContract,
        course: this.currentCourse.name,
        category: 'pos',
        chosen_extension_option: extensionOption.period,
        available_extension_options: this.extensionOptionsPeriods,
        available_extension_options_quantity: this.extensionOptionsPeriods.length,
        ...this.getTrialStatusByDescomplicaCourseId,
      });
    },
    trackContractExtensionButtonClick(extensionOption: LabeledExtensionOption): void {
      Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
        in_invalid_postgrad_study_planning_modal: true,
        button_action: 'contract_extension',
        days_to_finish_contract: this.daysToFinishContract,
        course: this.currentCourse.name,
        category: 'pos',
        chosen_extension_option: extensionOption.period,
        available_extension_options: this.extensionOptionsPeriods,
        available_extension_options_quantity: this.extensionOptionsPeriods.length,
        ...this.getTrialStatusByDescomplicaCourseId,
      });
    },
    isSecondToLastExtensionOption(option: LabeledExtensionOption): boolean {
      return (
        this.extensionOptionsForStudyPlanning.findIndex((o) => o.period === option.period) ===
        this.extensionOptionsForStudyPlanning.length - 2
      );
    },
  },
});
